/**
 * Auto-Login Service for Development/Preview Environments
 *
 * Automatically signs in with test credentials in local and preview environments.
 * This service runs during app initialization when `environment.autoLogin` is true.
 *
 * For local: Authenticates against Firebase Auth Emulator
 * For preview/staging: Authenticates against real Firebase Auth (test account)
 */
import { Injectable, inject } from '@angular/core';
import { Auth, signInWithEmailAndPassword, onAuthStateChanged, User } from '@angular/fire/auth';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AutoLoginService {
    private auth = inject(Auth);

    /**
     * Attempt auto-login if enabled in environment config.
     * Returns a promise that resolves when auth state is determined.
     */
    async tryAutoLogin(): Promise<boolean> {
        // Skip if auto-login is disabled
        if (!environment.autoLogin) {
            console.log('[Auth] Auto-login disabled in this environment');
            return false;
        }

        // Check if test user credentials are configured
        const testUser = (environment as any).testUser;
        if (!testUser?.email || !testUser?.password) {
            console.warn('[Auth] Auto-login enabled but testUser credentials not configured');
            return false;
        }

        // Wait for initial auth state to be determined
        const currentUser = await this.waitForAuthState();

        // Skip if already authenticated
        if (currentUser) {
            console.log(`[DEV] Already logged in as ${currentUser.email}`);
            return true;
        }

        // Attempt auto-login
        try {
            console.log(`[DEV] Auto-logging in as ${testUser.email}...`);
            const userCredential = await signInWithEmailAndPassword(
                this.auth,
                testUser.email,
                testUser.password
            );
            console.log(`[DEV] ✅ Auto-logged in as ${userCredential.user.email}`);
            return true;
        } catch (error: any) {
            console.error(`[DEV] ❌ Auto-login failed:`, error.message);

            // Provide helpful error message for common issues
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                if (environment.useEmulators) {
                    console.error('[DEV] Test user not found in emulator. Run: npm run emulators:seed');
                } else {
                    console.error(
                        '[DEV] Test user not found in Firebase. Create admin@test.local in Firebase Console.'
                    );
                }
            }

            return false;
        }
    }

    /**
     * Wait for Firebase Auth to determine initial auth state.
     * This is necessary because auth state is async on page load.
     */
    private waitForAuthState(): Promise<User | null> {
        return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(this.auth, (user) => {
                unsubscribe();
                resolve(user);
            });
        });
    }
}
